import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanAdminSeminarApplicationDetail,
  fetchYowanAdminSeminarApplicationList,
  fetchYowanAdminSeminarDetail,
  fetchYowanAdminSeminarSeriesDetail,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const messageColumnStyle = {
  minWidth: '36rem',
  width: '56%',
};

const seminarColumnStyle = {
  minWidth: '12rem',
  width: '15rem',
};

const applicantColumnStyle = {
  minWidth: '13rem',
  width: '15rem',
};

const scopeColumnStyle = {
  minWidth: '11rem',
  width: '13rem',
};

const appliedAtColumnStyle = {
  minWidth: '9rem',
  width: '10rem',
};

const actionColumnStyle = {
  minWidth: '10rem',
  width: '11rem',
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
  gap: '0.75rem',
  marginBlock: '0 1rem',
};

const summaryItemStyle = {
  display: 'grid',
  gap: '0.3rem',
  padding: '0.75rem 0.85rem',
  border: '1px solid rgba(47, 54, 61, 0.14)',
  borderRadius: '0.5rem',
  background: 'rgba(116, 126, 115, 0.08)',
};

const summaryLabelStyle = {
  margin: 0,
  color: 'rgba(47, 54, 61, 0.72)',
  fontSize: '0.78rem',
  fontWeight: 700,
};

const summaryValueStyle = {
  margin: 0,
  color: '#2f363d',
  fontSize: '1.12rem',
  fontWeight: 700,
  lineHeight: 1.3,
};

const messageTextStyle = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  lineHeight: 1.7,
  maxWidth: '62rem',
};

const actionListStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.45rem',
};

const normalizeMessageText = (value = '') => String(value || '').trim();

const buildMailHref = ({ seminarId, applicationId, preservedDomainSearch }) => {
  const searchParams = new URLSearchParams();
  const domain = new URLSearchParams(preservedDomainSearch).get('domain');

  if (domain) {
    searchParams.set('domain', domain);
  }

  searchParams.set('mode', 'single');
  searchParams.set('applicationId', applicationId);

  return `/admin/events/${seminarId}/applications/mail?${searchParams.toString()}`;
};

const hydrateApplicationsWithDetails = async (applications = []) => {
  const details = await Promise.all(
    applications.map((application) =>
      fetchYowanAdminSeminarApplicationDetail(application.id)
        .then((detail) => ({
          ...application,
          ...detail,
          eventId: application.eventId || detail.eventId,
          eventTitle: application.eventTitle || detail.eventTitle,
          eventSeries: application.eventSeries || detail.eventSeries,
        }))
        .catch(() => application),
    ),
  );

  return details;
};

const fetchSeminarApplicationsWithMessages = async (seminar) => {
  const seminarId = seminar?.id || '';

  if (!seminarId) {
    return [];
  }

  const envelope = await fetchYowanAdminSeminarApplicationList({ seminarId }).catch(() => ({ results: [] }));
  const hydratedApplications = await hydrateApplicationsWithDetails(envelope.results || []);

  return hydratedApplications.map((application) => ({
    ...application,
    seminarId,
    seminarTitle: seminar.title || application.eventTitle || '',
    seminarSubtitle: seminar.subtitle || '',
  }));
};

const AdminEventApplicationMessagesPage = () => {
  const { eventId, seriesId } = useParams();
  const preservedDomainSearch = usePreservedDomainSearch();
  const isSeriesMode = Boolean(seriesId);
  const content = yowanPageContent[
    isSeriesMode
      ? '/admin/events/series/:seriesId/application-messages'
      : '/admin/events/:eventId/application-messages'
  ];
  const [sourceRecord, setSourceRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');
    setRows([]);

    const load = async () => {
      if (isSeriesMode) {
        const series = await fetchYowanAdminSeminarSeriesDetail(seriesId);
        const seminars = Array.isArray(series?.seminars) ? series.seminars : [];
        const nestedRows = await Promise.all(seminars.map(fetchSeminarApplicationsWithMessages));

        return {
          source: series,
          rows: nestedRows.flat(),
        };
      }

      const seminar = await fetchYowanAdminSeminarDetail(eventId);
      const applications = await fetchSeminarApplicationsWithMessages(seminar);

      return {
        source: seminar,
        rows: applications,
      };
    };

    load()
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setSourceRecord(payload.source);
        setRows(payload.rows);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setSourceRecord(null);
        setRows([]);
        setLoadError('申込メッセージ一覧を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventId, isSeriesMode, seriesId]);

  const rowsWithMessages = useMemo(() => rows
    .map((row) => ({
      ...row,
      messageText: normalizeMessageText(row.message),
    }))
    .filter((row) => row.messageText), [rows]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleRows = normalizedQuery
    ? rowsWithMessages.filter((row) =>
        [
          row.seminarTitle,
          row.applicantName,
          row.applicantEmail,
          row.participationScopeLabel,
          row.slotParticipationLabel,
          row.messageText,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery)),
      )
    : rowsWithMessages;
  const headerTitle = sourceRecord
    ? `${sourceRecord.title || content.header.title} 申込メッセージ`
    : content.header.title;
  const relatedLinks = [
    { label: 'セミナー一覧', href: '/admin/events/seminars' },
    isSeriesMode && sourceRecord?.id
      ? { label: 'シリーズ詳細', href: `/admin/events/series/${sourceRecord.id}` }
      : null,
    !isSeriesMode && sourceRecord?.id
      ? { label: 'セミナー詳細', href: `/admin/events/${sourceRecord.id}` }
      : null,
    !isSeriesMode && sourceRecord?.id
      ? { label: '参加者一覧', href: `/admin/events/${sourceRecord.id}/applications` }
      : null,
  ].filter(Boolean);

  if (!content) {
    return null;
  }

  return (
    <AdminBlogShell activeNavHref="/admin/events/seminars" routeKey={content.routeKey}>
      <AdminPageHeader
        title={headerTitle}
        paragraphs={content.header.paragraphs}
        actions={relatedLinks.map((link) => (
          <Link key={link.href} to={appendYowanPreviewQueryIfNeeded(link.href, preservedDomainSearch)}>
            {link.label}
          </Link>
        ))}
      />

      <div className="c-surface-content" style={summaryGridStyle}>
        <div style={summaryItemStyle}>
          <p style={summaryLabelStyle}>申込件数</p>
          <p style={summaryValueStyle}>{rows.length}件</p>
        </div>
        <div style={summaryItemStyle}>
          <p style={summaryLabelStyle}>メッセージあり</p>
          <p style={summaryValueStyle}>{rowsWithMessages.length}件</p>
        </div>
        <div style={summaryItemStyle}>
          <p style={summaryLabelStyle}>表示中</p>
          <p style={summaryValueStyle}>{visibleRows.length}件</p>
        </div>
      </div>

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        sortValue="created-desc"
        onSortChange={() => {}}
        metaText={`${rowsWithMessages.length}件中 ${visibleRows.length}件を表示`}
        searchPlaceholder="セミナー / 氏名 / メールアドレス / メッセージ"
        sortOptions={[{ value: 'created-desc', label: '申込日が新しい順' }]}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label="申込メッセージ一覧を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? (
          <p className="yowan-admin-form__message">{loadError}</p>
        ) : null}
        {!isLoading && !loadError && !visibleRows.length ? (
          <p className="yowan-admin-form__message">表示できる申込メッセージはありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRows.length ? (
          <table className="yowan-admin-resource-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col" style={appliedAtColumnStyle}>申込日</th>
                <th scope="col" style={seminarColumnStyle}>セミナー</th>
                <th scope="col" style={applicantColumnStyle}>申込者</th>
                <th scope="col" style={scopeColumnStyle}>参加対象</th>
                <th scope="col" style={messageColumnStyle}>申込メッセージ</th>
                <th scope="col" style={actionColumnStyle}>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={row.id || `${row.seminarId}-${index}`}>
                  <td>{index + 1}</td>
                  <td style={appliedAtColumnStyle}>{row.createdAt || '-'}</td>
                  <td style={seminarColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.seminarTitle || row.eventTitle || '-'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.seminarSubtitle || row.eventSeries?.title || '-'}</p>
                  </td>
                  <td style={applicantColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.applicantName || '-'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.applicantEmail || '-'}</p>
                  </td>
                  <td style={scopeColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.participationScopeLabel || '-'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.slotParticipationLabel || '-'}</p>
                  </td>
                  <td style={messageColumnStyle}>
                    <p style={messageTextStyle}>{row.messageText}</p>
                  </td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions" style={actionListStyle}>
                      <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref || `/admin/events/seminars/applications/${row.id}`, preservedDomainSearch)}>
                        詳細
                      </Link>
                      <Link
                        to={buildMailHref({
                          seminarId: row.seminarId || row.eventId,
                          applicationId: row.id,
                          preservedDomainSearch,
                        })}
                      >
                        個別連絡
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminEventApplicationMessagesPage;
